import { Wallet, Building2, Bitcoin, CreditCard, Copy, Check } from 'lucide-react';
import type { Wallet as WalletType } from '../../lib/api';

export type WalletCategory = 'cash' | 'bank' | 'crypto' | 'payment_system';

export const WALLET_CATEGORIES: WalletCategory[] = ['cash', 'bank', 'crypto', 'payment_system'];

export const CATEGORY_ICONS: Record<WalletCategory, React.ReactNode> = {
  cash: <Wallet size={18} />,
  bank: <Building2 size={18} />,
  crypto: <Bitcoin size={18} />,
  payment_system: <CreditCard size={18} />,
};

export const CATEGORY_LABELS: Record<WalletCategory, string> = {
  cash: 'Cash',
  bank: 'Bank',
  crypto: 'Crypto',
  payment_system: 'Payment',
};

export interface WalletCardProps {
  wallet: WalletType;
  onCopyAddress: (address: string, walletId: string) => void;
  isCopied: boolean;
  onClick: () => void;
  balance?: string;
}

export function WalletCard({ wallet, onCopyAddress, isCopied, onClick, balance }: WalletCardProps) {
  const category = wallet.type as WalletCategory;

  const truncateAddress = (address: string) => {
    if (address.length <= 16) return address;
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const formatBalance = (value: string) => {
    const num = parseFloat(value);
    const isCrypto = category === 'crypto';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: isCrypto ? 6 : 2,
    }).format(num);
  };

  const iconColorClasses: Record<WalletCategory, string> = {
    cash: 'bg-ctp-green/15 text-ctp-green',
    bank: 'bg-ctp-mauve/15 text-ctp-lavender',
    crypto: 'bg-ctp-peach/15 text-ctp-yellow',
    payment_system: 'bg-ctp-blue/15 text-ctp-sky',
  };

  const badgeColorClasses: Record<WalletCategory, string> = {
    cash: 'bg-ctp-green/15 text-ctp-green',
    bank: 'bg-ctp-mauve/15 text-ctp-lavender',
    crypto: 'bg-ctp-peach/15 text-ctp-yellow',
    payment_system: 'bg-ctp-blue/15 text-ctp-sky',
  };

  return (
    <div
      className={`bg-surface border border-border rounded-xl p-4 transition-all shadow-sm cursor-pointer hover:shadow-md ${!wallet.isActive ? 'opacity-50' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${iconColorClasses[category]}`}>
          {CATEGORY_ICONS[category] || <Wallet size={16} />}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-foreground m-0">{wallet.name}</h3>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-foreground-muted">{wallet.currencyCode}</span>
            <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium uppercase ${badgeColorClasses[category]}`}>
              {CATEGORY_LABELS[category] || wallet.type}
            </span>
          </div>
        </div>
        {balance !== undefined && (
          <div className="text-right flex-shrink-0">
            <div className="text-[10px] font-medium text-foreground-muted uppercase tracking-wide mb-0.5">Balance</div>
            <div className="text-base font-bold text-foreground leading-none">
              {formatBalance(balance)}
              <span className="text-xs font-normal text-foreground-muted ml-1">{wallet.currencyCode}</span>
            </div>
          </div>
        )}
      </div>

      <div className="pt-3 border-t border-border">
        <div className="text-[10px] font-medium text-foreground-muted uppercase tracking-wide mb-1">Details / Address</div>
        <div className="flex items-center gap-2">
          {wallet.address ? (
            <>
              <span className="flex-1 font-mono text-xs text-foreground-subtle overflow-hidden text-ellipsis whitespace-nowrap" title={wallet.address}>
                {truncateAddress(wallet.address)}
              </span>
              <button
                className={`bg-transparent border-none p-1 cursor-pointer rounded flex items-center justify-center flex-shrink-0 transition-all hover:bg-surface-hover ${isCopied ? 'text-ctp-green' : 'text-foreground-muted hover:text-foreground-subtle'}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onCopyAddress(wallet.address!, wallet.id);
                }}
                title="Copy address"
              >
                {isCopied ? <Check size={12} /> : <Copy size={12} />}
              </button>
            </>
          ) : (
            <span className="text-xs text-foreground-muted italic">No address configured</span>
          )}
        </div>
      </div>

      {wallet.description && (
        <div className="text-xs text-foreground-subtle mt-2">
          <span className="text-foreground-muted">Note: </span>
          {wallet.description}
        </div>
      )}
    </div>
  );
}
